import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import UnderlineExt from '@tiptap/extension-underline';
import Typography from '@tiptap/extension-typography';
import type { Editor } from '@tiptap/core';
import {
  CharacterName,
  Dialogue,
  Parenthetical,
  StageDirection,
  ActHeading,
  SceneHeading,
  PlaywritingKeymap,
  PasteHandler,
  CharacterAutoComplete,
} from '../../extensions';
import { FindReplaceExtension } from '../../extensions/FindReplaceExtension';
import { usePersistence, type SaveStatus, type PersistenceBlob } from '../../hooks/usePersistence';
import { exportToPdf, printPdf } from '../../utils/pdfExport';
import { CharacterSuggestions } from './CharacterSuggestions';
import { FindReplace } from './FindReplace';

const ELEMENT_TYPES = [
  { value: 'characterName', label: 'CHARACTER' },
  { value: 'dialogue', label: 'DIALOGUE' },
  { value: 'parenthetical', label: 'PARENTHETICAL' },
  { value: 'stageDirection', label: 'STAGE DIRECTION' },
  { value: 'actHeading', label: 'ACT HEADING' },
  { value: 'sceneHeading', label: 'SCENE HEADING' },
] as const;

const FONTS = [
  { value: "'Courier New', monospace", label: 'Courier New' },
  { value: "'Times New Roman', serif", label: 'Times New Roman' },
];

interface SoliloquyEditorProps {
  globalFont: string;
  title: string;
  onTitleChange: (title: string) => void;
  onEditorReady?: (editor: Editor) => void;
}

function getStatusText(status: SaveStatus): string {
  switch (status.state) {
    case 'idle': return '';
    case 'saving': return 'Saving...';
    case 'saved': return `Saved at ${status.timestamp}`;
    case 'error': return `Save failed — ${status.message}`;
  }
}

function countWords(editor: Editor): number {
  let count = 0;
  editor.state.doc.descendants((node) => {
    if (node.isText && node.text) {
      const words = node.text.trim().split(/\s+/).filter(Boolean);
      count += words.length;
    }
  });
  return count;
}

function countSelectedWords(editor: Editor): number {
  const { from, to } = editor.state.selection;
  if (from === to) return 0;
  const text = editor.state.doc.textBetween(from, to, ' ');
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function SoliloquyEditor({ globalFont, title, onTitleChange, onEditorReady }: SoliloquyEditorProps) {
  const [currentNodeType, setCurrentNodeType] = useState<string>('characterName');
  const [selectedFont, setSelectedFont] = useState(FONTS[0].value);
  const [wordCount, setWordCount] = useState(0);
  const [selectedWordCount, setSelectedWordCount] = useState(0);
  const [showFindReplace, setShowFindReplace] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        underline: false,
      }),
      UnderlineExt,
      Typography,
      Placeholder.configure({
        placeholder: 'Start writing your play...',
        includeChildren: true,
      }),
      CharacterName,
      Dialogue,
      Parenthetical,
      StageDirection,
      ActHeading,
      SceneHeading,
      PasteHandler,
      FindReplaceExtension,
      // CharacterAutoComplete before PlaywritingKeymap: both use ProseMirror
      // handleKeyDown. AutoComplete gets first crack — if suggestions are
      // showing, it handles Enter/Tab. Otherwise returns false and
      // PlaywritingKeymap handles element type switching.
      CharacterAutoComplete,
      PlaywritingKeymap,
    ],
    content: {
      type: 'doc',
      content: [{ type: 'characterName' }],
    },
    editorProps: {
      attributes: {
        class: 'soliloquy-editor-content outline-none min-h-[60vh]',
        spellcheck: 'true',
      },
    },
    onSelectionUpdate: ({ editor }) => {
      const { $from } = editor.state.selection;
      setCurrentNodeType($from.parent.type.name);
      setSelectedWordCount(countSelectedWords(editor));
    },
    onUpdate: ({ editor }) => {
      const { $from } = editor.state.selection;
      setCurrentNodeType($from.parent.type.name);
      setWordCount(countWords(editor));
    },
  });

  // Notify parent when editor is ready
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Initial word count
  useEffect(() => {
    if (editor) setWordCount(countWords(editor));
  }, [editor]);

  const { saveStatus, save, load, metadata, setMetadata } = usePersistence(editor, title);

  // Load saved document on mount
  useEffect(() => {
    if (!editor) return;
    const blob = load();
    if (blob?.document) {
      editor.commands.setContent(blob.document);
      if (blob.title) onTitleChange(blob.title);
      // Recount after loading
      setWordCount(countWords(editor));
    }
  }, [editor]);

  const handleExportPdf = useCallback(async () => {
    if (!editor) return;
    const json = editor.getJSON();
    const blob: PersistenceBlob = {
      version: 1,
      title,
      metadata,
      lastSaved: new Date().toISOString(),
      document: json,
    };
    await exportToPdf(blob);
  }, [editor, title, metadata]);

  const handlePrint = useCallback(async () => {
    if (!editor) return;
    const json = editor.getJSON();
    const blob: PersistenceBlob = {
      version: 1,
      title,
      metadata,
      lastSaved: new Date().toISOString(),
      document: json,
    };
    await printPdf(blob);
  }, [editor, title, metadata]);

  // Ctrl+S save, Ctrl+P print, Ctrl+F find, Ctrl+H find/replace
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        save();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowFindReplace(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setShowFindReplace(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [save, handlePrint]);

  // Update font on editor element
  useEffect(() => {
    if (!editor) return;
    const el = editor.view.dom as HTMLElement;
    el.style.fontFamily = `${selectedFont}`;
    el.style.fontSize = '12pt';
    el.style.lineHeight = '1.5';
  }, [editor, selectedFont]);

  const handleSetNodeType = useCallback((type: string) => {
    if (!editor) return;
    editor.chain().focus().setNode(type).run();
  }, [editor]);

  if (!editor) return null;

  const statusText = getStatusText(saveStatus);
  const pageEstimate = Math.max(1, Math.round(wordCount / 250));
  const wordDisplay = selectedWordCount > 0
    ? `${selectedWordCount.toLocaleString()} / ${wordCount.toLocaleString()} words`
    : `${wordCount.toLocaleString()} words`;

  return (
    <div className="soliloquy-editor">
      {/* Find/Replace bar */}
      {showFindReplace && (
        <FindReplace
          editor={editor}
          onClose={() => setShowFindReplace(false)}
        />
      )}

      <EditorContent editor={editor} />
      <CharacterSuggestions editor={editor} />

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-ink-900 border-t border-ink-200 dark:border-ink-700 px-4 py-1.5 flex items-center justify-between z-10">
        {/* Left: Element type selector + formatting */}
        <div className="flex items-center gap-3">
          <select
            value={currentNodeType}
            onChange={(e) => handleSetNodeType(e.target.value)}
            className="text-[11px] text-ink-600 dark:text-ink-400 bg-ink-50 dark:bg-ink-800 border border-ink-200 dark:border-ink-700 rounded px-2 py-1 cursor-pointer hover:bg-ink-100 dark:hover:bg-ink-700 transition-colors"
          >
            {ELEMENT_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <div className="flex items-center gap-0.5 border-l border-ink-200 dark:border-ink-700 pl-3">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                editor.isActive('bold')
                  ? 'bg-ink-200 dark:bg-ink-700 text-ink-900 dark:text-ink-100'
                  : 'text-ink-400 dark:text-ink-500 hover:text-ink-600 dark:hover:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800'
              }`}
              title="Bold (Ctrl+B)"
            >
              <strong>B</strong>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                editor.isActive('italic')
                  ? 'bg-ink-200 dark:bg-ink-700 text-ink-900 dark:text-ink-100'
                  : 'text-ink-400 dark:text-ink-500 hover:text-ink-600 dark:hover:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800'
              }`}
              title="Italic (Ctrl+I)"
            >
              <em>I</em>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`px-1.5 py-0.5 text-xs rounded transition-colors ${
                editor.isActive('underline')
                  ? 'bg-ink-200 dark:bg-ink-700 text-ink-900 dark:text-ink-100'
                  : 'text-ink-400 dark:text-ink-500 hover:text-ink-600 dark:hover:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800'
              }`}
              title="Underline (Ctrl+U)"
            >
              <span className="underline">U</span>
            </button>
          </div>

          <div className="border-l border-ink-200 dark:border-ink-700 pl-3">
            <select
              value={selectedFont}
              onChange={(e) => setSelectedFont(e.target.value)}
              className="text-[11px] text-ink-600 dark:text-ink-400 bg-ink-50 dark:bg-ink-800 border border-ink-200 dark:border-ink-700 rounded px-2 py-1 cursor-pointer"
            >
              {FONTS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Center: Word/page count + keyboard hints */}
        <div className="flex items-center gap-4">
          <span className="text-[11px] text-ink-400 dark:text-ink-500">
            ~{pageEstimate} {pageEstimate === 1 ? 'page' : 'pages'} · {wordDisplay}
          </span>
          <span className="text-[11px] text-ink-300 dark:text-ink-600 tracking-wide hidden lg:block">
            Tab: cycle · Enter: next · Shift+Tab: reverse
          </span>
        </div>

        {/* Right: Export + Save status */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportPdf}
            className="text-[11px] text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 transition-colors"
            title="Export PDF (Ctrl+P)"
          >
            Export PDF
          </button>
          <span className={`text-[11px] ${
            saveStatus.state === 'error' ? 'text-red-500' :
            saveStatus.state === 'saving' ? 'text-ink-400 dark:text-ink-500' :
            'text-ink-300 dark:text-ink-600'
          }`}>
            {statusText}
          </span>
        </div>
      </div>
    </div>
  );
}
